import React, { type ReactElement } from 'react'
import { Box, Tooltip } from '@mui/material'

import Icon from '../Icon'
import IconButton from '../IconButton'
import Search, { type SearchProps } from '../Search'

import './Table.scss'

export interface TableToolbarSearchProps extends Pick<
  SearchProps,
  'value' | 'onChange' | 'onClear' | 'placeholder' | 'autoFocus'
> {}

export interface TableToolbarProps {
  /**
   * Search bar configuration. When omitted the search control is not rendered.
   * Uses the internal `Search` component so styling stays consistent with the
   * rest of the design system.
   */
  search?: TableToolbarSearchProps
  /**
   * Filter slot — typically the internal `<Filter />` component plus an optional
   * `<FilterChipBar />` below the toolbar. Rendered verbatim so callers keep
   * full control over filter state.
   */
  filters?: React.ReactNode
  /**
   * Handler for the download / export action. When omitted the button is not rendered.
   */
  onDownload?: (event: React.MouseEvent<HTMLElement>) => void
  /** Accessible label for the download button. Defaults to "Download". */
  downloadLabel?: string
  /**
   * Additional action content rendered on the right side of the toolbar
   * (e.g. a primary "New item" button).
   */
  actions?: React.ReactNode
}

const TableToolbar = ({
  search,
  filters,
  onDownload,
  downloadLabel = 'Download',
  actions,
}: TableToolbarProps): ReactElement => {
  return (
    <Box className='internal-table-toolbar' role='toolbar' aria-label='Table controls'>
      <Box className='internal-table-toolbar__left'>
        {search && (
          <Box className='internal-table-toolbar__search'>
            <Search {...search} />
          </Box>
        )}
        {filters}
      </Box>
      <Box className='internal-table-toolbar__right'>
        {onDownload && (
          <Tooltip title={downloadLabel}>
            <IconButton size='small' aria-label={downloadLabel} onClick={onDownload}>
              <Icon name='download' />
            </IconButton>
          </Tooltip>
        )}
        {actions}
      </Box>
    </Box>
  )
}

export default TableToolbar
